import axios from './common';

if (process.env.VUE_APP_FAKE_SERVICES === 'true') {
    require('./fake/videos.js');
}

const state = {
    videos: [],
    aspectRatios: [
        '4:3', '1.33:1', '16:9', '1.85:1', '2.39:1', '2.76:1', '1.37:1',
    ],
    videosQueryParams: {
        description: null,
        limit: 10,
        page: 1,
    },
    currentVideoItem: null,
    editableVideoItem: null,
    videosLoading: false,
    videoItemLoading: false,
    videoApiError: null,
    totalVideos: 0,
};

const getters = {
    aspectRatios: state => {
        return state.aspectRatios;
    },
    videos: state => {
        return state.videos;
    },
    videosQueryParams: state => {
        return state.videosQueryParams;
    },
    videosLoading: state => {
        return state.videosLoading;
    },
    currentVideoItem: state => {
        return state.currentVideoItem;
    },
    editableVideoItem: state => {
        return state.editableVideoItem;
    },
    videoItemLoading: state => {
        return state.videoItemLoading;
    },
    videoApiError: state => {
        return state.videoApiError;
    },
    totalVideos: state => {
        return state.totalVideos;
    },
};

const actions = {
    getVideos(context) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: 'Loading videos',
        }, {root: true}).then(() => context.commit('setVideosLoading', true));

        axios.get(`${process.env.VUE_APP_SERVICE_URI_VIDEO}/api/vimeo-youtube/`, {
            params: context.state.videosQueryParams,
        })
            .then(response => {
                context.dispatch('addNotification', {
                    status: 'success',
                    message: 'Videos successfully loaded',
                }, {root: true}).then(function() {
                    context.commit('setVideosLoading', false);
                    context.commit('setVideos', response.data);
                });
            });
    },
    async deleteVideo(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Deleting Video "${item.title}"`,
        }, {root: true}).then(() => context.commit('setVideoItemLoading', true));

        try {
            await axios.delete(`${process.env.VUE_APP_SERVICE_URI_VIDEO}/api/vimeo-youtube/${item._id}`);
            await context.dispatch('addNotification', {
                status: 'success',
                message: `Successfully deleted Video "${item.title}"`,
            }, {root: true});
            return true;
        } catch(error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Video "${item.title}" was deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Delete failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('setVideoApiError', error);
                    console.error('videoApiError', error);
                });
            }
            return false;
        } finally {
            context.commit('setVideoItemLoading', false);
            await context.dispatch('clearVideoItems');
            await context.dispatch('getVideos');
        }
    },
    async getVideo(context, item) {
        context.dispatch('addNotification', {
            status: 'loading',
            message: `Retrieving Video "${item.title}"`,
        }, {root: true}).then(function () {
            context.commit('setVideoItemLoading', true);
            context.commit('setVideoApiError', null);
        });

        try {
            const response = await axios.get(`${process.env.VUE_APP_SERVICE_URI_VIDEO}/api/vimeo-youtube/${item._id}`);
            if (response) {
                context.commit('setCurrentVideoItem', response.data);
            }
        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Video "${item.title}" deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Retrieval failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    console.error('videoApiError', error);
                });
            }
        } finally {
            context.commit('setVideoItemLoading', false);
        }
    },
    async editVideo(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Editing Video "${item.title}"`,
        }, {root: true}).then(function() {
            context.commit('setVideoItemLoading', true);
            context.commit('setVideoApiError', null);
        });

        try {
            await axios.put(`${process.env.VUE_APP_SERVICE_URI_VIDEO}/api/vimeo-youtube/${item._id}`, item);

            await context.dispatch('addNotification', {
                status: 'success',
                message: `Video "${item.title}" successfully edited `,
            }, {root: true});

            return true;

        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Video "${item.title}" was deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Edit failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('videoApiError', error);
                    console.error('videoApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setVideoItemLoading', false);
            await context.dispatch('clearVideoItems');
            await context.dispatch('getVideos');
        }
    },
    async addVideo(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Adding Video "${item.title}"`,
        }, {root: true}).then(function () {
            context.commit('setVideoItemLoading', true);
            context.commit('setVideoApiError', null);
        });

        try {

            await axios.post(`${process.env.VUE_APP_SERVICE_URI_VIDEO}/api/vimeo-youtube/`, item);

            await context.dispatch('addNotification', {
                status: 'success',
                message: `Video "${item.title}" successfully added`,
            }, {root: true});

            return true;

        } catch (error) {
            if (error.response) {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Failed to add Video "${item.title}", please try again`,
                }, {root: true}).then(() => {
                    context.commit('setVideoApiError', error.response.data);
                });
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Add failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    context.commit('setVideoApiError', error);
                    console.error('videoApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setVideoItemLoading', false);
            await context.dispatch('getVideos');
        }
    },
    clearVideoItems(context) {
        context.commit('setCurrentVideoItem', null);
        context.commit('setEditableVideoItem', null);
    },
    clearVideoApiError(context) {
        context.commit('setVideoApiError', null);
    },
    setVideoPaginationOptions(context, options) {
        context.commit('videoPaginationOptions', options);
    },
    setVideosSearch(context, search) {
        context.commit('videosSearch', search);
    },
};

const mutations = {
    videoPaginationOptions(state, options) {
        state.videosQueryParams.limit = options.itemsPerPage ?? 10;
        state.videosQueryParams.page = options.page ?? 1;
    },
    videosSearch(state, search) {
        state.videosQueryParams.description = `/${search.trim()}/i`;
    },
    setVideos(state, responseData) {
        state.videos = responseData.docs;
        state.totalVideos = responseData.totalDocs;
    },
    setCurrentVideoItem(state, item) {
        state.currentVideoItem = item;
        state.editableVideoItem = {...item};
    },
    setEditableVideoItem(state, item) {
        state.editableVideoItem = item;
    },
    setVideoItemLoading(state, isLoading) {
        state.videoItemLoading = isLoading;
    },
    setVideosLoading(state, isLoading) {
        state.videosLoading = isLoading;
    },
    setVideoApiError(state, error) {
        state.videoApiError = error;
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};